import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton, DetailSkeleton } from "@/components/Skeletons";
import { EditFreelancerProfileModal } from "@/components/EditFreelancerProfileModal";
import { CreateFreelancerModal } from "@/components/CreateFreelancerModal";
import { ReassignAssignmentModal } from "@/components/ReassignAssignmentModal";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { ChipRow, SpecialisationRows } from "@/components/build/SpecialisationDisplay";
import { jobExperienceLabel } from "@/components/build/InterviewContextZone";
import { normalizeUrl } from "@/lib/normalizeUrl";
import {
  UserCheck, MapPin, ShieldCheck, Camera, Aperture, Sun, Mic, Plane, Clapperboard,
  Globe, Instagram, Youtube, ExternalLink, Wallet, Star, Award, Languages, Film, Pencil,
  UserPlus, Archive, RotateCcw, Check, Eye, EyeOff, ChevronUp, ChevronDown, MoreHorizontal, X,
} from "lucide-react";
import { HealthMarker } from "@/components/HealthMarker";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";

const AVAILABILITY_OPTIONS = ["Available", "Busy", "Unavailable"];
const AVAILABILITY_STYLES = {
  available: "bg-emerald-500/10 text-emerald-600",
  busy: "bg-brand/15 text-ink",
  unavailable: "bg-hairline text-[#78716C]",
};

// Phase 7 (2026-10, Job E) — compact sortable table columns (frame 12). "skills" isn't
// sortable (free text).
const BUILD_TABLE_COLUMNS = [
  { key: "name", label: "Member" },
  { key: "primary_role", label: "Role" },
  { key: "canonical_location", label: "Location" },
  { key: "availability", label: "Availability" },
  { key: "needs_interview_review", label: "Interview" },
  { key: "partner_agency_visible", label: "Agency" },
  { key: "skills", label: "Skills", sortable: false },
];

// Phase 7 (2026-10, Job E) — DISPLAY-ONLY split of the free-text `skills` field at "|| Gear:".
// Confirmed from production data (rm_freelancers, 2026-10): only 41 of 79 freelancers with a
// non-empty `skills` value actually contain "||" — NOT reliable in every record (see PR notes
// for the "confirm first" answer). When the separator isn't present, the raw text is shown
// unsplit as Skills, exactly as it already renders today. Stored data is never rewritten.
function splitSkillsGear(skills) {
  if (!skills) return { skills: null, gear: null };
  const idx = skills.indexOf("||");
  if (idx === -1) return { skills: skills.trim() || null, gear: null };
  const before = skills.slice(0, idx).trim().replace(/\|\s*$/, "").trim();
  const after = skills.slice(idx + 2).trim().replace(/^Gear:\s*/i, "");
  return { skills: before || skills.trim(), gear: after || null };
}

function Initials({ name, className = "h-14 w-14 text-base" }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className={`rounded-full bg-brand/15 border border-brand/30 flex items-center justify-center font-semibold text-ink shrink-0 ${className}`}>
      {initials}
    </div>
  );
}

function AvailabilityChip({ availability }) {
  if (!availability) return <span className="rounded-full bg-hairline px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-faint">Not specified</span>;
  const cls = AVAILABILITY_STYLES[availability.toLowerCase()] || "bg-hairline text-ink-soft";
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider whitespace-nowrap ${cls}`}>{availability}</span>;
}

function VerifiedBadge({ badges }) {
  if (!(badges || []).includes("Verified")) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-ink">
      <ShieldCheck className="h-3 w-3" /> NJM Verified
    </span>
  );
}

function MemberTableRow({ f, selectMode, selected, onToggleSelect, isActive, onOpen }) {
  const { skills } = splitSkillsGear(f.skills);
  const handleRowClick = () => { if (selectMode) onToggleSelect(f.id); else onOpen(f.id); };
  return (
    <tr data-testid={`build-directory-row-${f.id}`} onClick={handleRowClick}
      className={`h-14 cursor-pointer border-b border-hairline last:border-b-0 hover:bg-surface2 ${isActive ? "bg-brand/10" : ""} ${f.archived ? "opacity-60" : ""}`}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-2.5 min-w-0">
          {selectMode && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onToggleSelect(f.id); }} data-testid={`build-select-${f.id}`}
              aria-label="Select freelancer"
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? "border-brand bg-brand text-ink" : "border-hairline bg-paper text-transparent"}`}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </button>
          )}
          {f.profile_image ? (
            <ProfileAvatar freelancerId={f.id} hasPhoto alt={f.name} className="h-8 w-8 rounded-full object-cover shrink-0 ring-1 ring-hairline" testId={`build-profile-avatar-${f.id}`} />
          ) : (
            <Initials name={f.name} className="h-8 w-8 text-[11px]" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink truncate" data-testid={`build-profile-name-${f.id}`}>{f.name}</div>
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
              {f.archived && <span data-testid={`build-archived-badge-${f.id}`} className="text-[10px] font-medium uppercase tracking-wider text-tone-red-text">Archived</span>}
              {!f.archived && f.approved === false && <span data-testid={`build-pending-approval-badge-${f.id}`} className="text-[10px] font-medium uppercase tracking-wider text-tone-gold-text">Pending</span>}
              {f.test_account && <span data-testid={`build-test-account-badge-${f.id}`} className="text-[10px] font-medium uppercase tracking-wider text-tone-blue-text">Test</span>}
              <VerifiedBadge badges={f.badges} />
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap">{f.primary_role || "—"}</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">
        {f.canonical_location ? <span className="inline-flex items-center gap-1 text-ink-soft"><MapPin className="h-3 w-3" />{f.canonical_location}</span> : <span className="text-faint">—</span>}
      </td>
      <td className="px-3 py-2"><AvailabilityChip availability={f.availability} /></td>
      <td className="px-3 py-2">
        {f.needs_interview_review ? <HealthMarker health="needs_attention" label="Needs Interview Review" testId={`build-needs-review-badge-${f.id}`} /> : <span className="text-faint text-xs">—</span>}
      </td>
      <td className="px-3 py-2">
        {f.partner_agency_visible ? (
          <span data-testid={`build-agency-visible-badge-${f.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-tone-green-text"><Eye className="h-3 w-3" />Visible</span>
        ) : <span className="text-faint text-xs">—</span>}
      </td>
      <td className="px-3 py-2 min-w-0 max-w-[220px]">
        {skills ? <div className="truncate text-xs text-ink-soft">{skills}</div> : <span className="text-faint text-xs">—</span>}
      </td>
    </tr>
  );
}

const PaneField = ({ label, value, testId }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-faint">{label}</div>
    <div className="text-sm text-ink mt-0.5" data-testid={testId}>{value}</div>
  </div>
);

// Phase 7 (2026-10, Job E) — "⋯" only ever holds actions that already exist on the Freelancer
// Profile page today (Edit Profile / Archive·Reactivate / agency-visibility toggle), calling
// the EXACT SAME endpoints that page already calls — no new capability, just reachable from
// the list without a full navigation. Hidden entirely when canManage is false (same
// empty-overflow rule as Jobs C/D).
function MemberPane({ member, canManage, canToggleAgency, busy, onClose, onEdit, onToggleArchive, onToggleAgencyVisible }) {
  const { skills, gear } = splitSkillsGear(member.skills);
  return (
    <div className="hidden lg:block w-[320px] shrink-0 sticky top-4 self-start rounded-lg border border-hairline bg-paper" data-testid="build-directory-pane">
      <div className="flex items-start justify-between gap-2 p-4 border-b border-hairline">
        <div className="flex items-center gap-3 min-w-0">
          {member.profile_image ? (
            <ProfileAvatar freelancerId={member.id} hasPhoto alt={member.name} className="h-14 w-14 rounded-full object-cover shrink-0 ring-1 ring-hairline" testId="build-directory-pane-avatar" />
          ) : (
            <Initials name={member.name} />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink truncate" data-testid="build-directory-pane-name">{member.name}</div>
            <div className="text-xs text-ink-soft truncate">{member.primary_role || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label="More actions" data-testid="build-directory-pane-overflow-btn"
                  className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-7 w-7 hover:border-brand">
                  <MoreHorizontal className="h-3.5 w-3.5 text-ink-soft" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 p-1.5">
                <button type="button" data-testid="build-directory-pane-edit" disabled={busy} onClick={onEdit}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2 disabled:opacity-50">
                  Edit Profile
                </button>
                {canToggleAgency && (
                  <button type="button" data-testid="build-directory-pane-toggle-agency" onClick={() => onToggleAgencyVisible(!member.partner_agency_visible)}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2">
                    {member.partner_agency_visible ? "Hide from agencies" : "Show to agencies"}
                  </button>
                )}
                <button type="button" data-testid="build-directory-pane-toggle-archive" disabled={busy} onClick={() => onToggleArchive(!member.archived)}
                  className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] disabled:opacity-50 ${member.archived ? "text-ink-soft hover:bg-surface2" : "text-tone-red-text hover:bg-tone-red-bg"}`}
                >
                  {member.archived ? "Reactivate" : "Archive"}
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button type="button" aria-label="Close" onClick={onClose} data-testid="build-directory-pane-close"
            className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-7 w-7 hover:border-brand text-ink-soft">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <AvailabilityChip availability={member.availability} />
          {member.needs_interview_review && <HealthMarker health="needs_attention" label="Needs Interview Review" testId="build-directory-pane-needs-review" />}
        </div>
        <PaneField label="Location" value={member.canonical_location || "—"} testId="build-directory-pane-location" />
        <PaneField label="Agency" value={member.partner_agency_visible ? "Visible to agencies" : "Not visible to agencies"} testId="build-directory-pane-agency" />
        {skills && <PaneField label="Skills" value={skills} testId="build-directory-pane-skills" />}
        {gear && <PaneField label="Gear" value={gear} testId="build-directory-pane-gear" />}
        <Link to={`/build/f/${member.id}`} data-testid="build-directory-pane-open-profile" className="inline-flex items-center gap-1 text-xs font-medium text-gold-text hover:underline pt-1">
          Open full profile <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

export function BuildDirectory() {
  const { hasScope } = useAuth();
  const canManage = hasScope("freelancer_profile.write");
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skillInput, setSkillInput] = useState(sp.get("skill") || "");
  const [showArchived, setShowArchived] = useState(false);
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Phase 7 (2026-10, Job E) — sortable table (client-side only, same fetched `items`) +
  // sticky pane for whichever row was last clicked.
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [selectedId, setSelectedId] = useState(null);
  const [paneBusy, setPaneBusy] = useState(false);
  const [paneFreelancer, setPaneFreelancer] = useState(null);

  const toggleSelect = (fid) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(fid) ? next.delete(fid) : next.add(fid);
    return next;
  });
  const bulkSetVisibility = async (visible) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await api.post("/v1/build/freelancers/agency-visibility/bulk", { freelancer_ids: [...selected], visible });
      toast.success(`${selected.size} freelancer(s) ${visible ? "shown to" : "hidden from"} agencies`);
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch { toast.error("Bulk update failed"); } finally { setBulkBusy(false); }
  };

  const role = sp.get("role") || "";
  const location = sp.get("location") || "";
  const skill = sp.get("skill") || "";
  const availability = sp.get("availability") || "";

  useEffect(() => {
    api.get("/v1/build/roles").then((r) => setRoles(r.data.items || []));
    api.get("/v1/build/locations").then((r) => setLocations(r.data.items || []));
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (role) params.role = role;
    if (location) params.location = location;
    if (skill) params.skill = skill;
    if (availability) params.availability = availability;
    if (showArchived) { params.include_archived = true; params.include_pending = true; }
    if (showTestAccounts) params.include_test_accounts = true;
    api.get("/v1/build/freelancers", { params }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, [role, location, skill, availability, showArchived, showTestAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value); else next.delete(key);
    setSp(next);
  };

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortedItems = [...items].sort((a, b) => {
    const av = a[sort.key]; const bv = b[sort.key];
    let cmp;
    if (typeof av === "boolean" || typeof bv === "boolean") cmp = (av ? 1 : 0) - (bv ? 1 : 0);
    else cmp = (av || "").toString().toLowerCase().localeCompare((bv || "").toString().toLowerCase());
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const selectedMember = items.find((f) => f.id === selectedId) || null;
  const canToggleAgency = canManage && hasScope("build.write") && !!selectedMember && !selectedMember.archived;

  const openPaneEdit = async () => {
    if (!selectedMember) return;
    setPaneBusy(true);
    try {
      const r = await api.get(`/v1/build/freelancers/${selectedMember.id}`);
      setPaneFreelancer(r.data.freelancer);
    } catch { toast.error("Couldn't load freelancer"); } finally { setPaneBusy(false); }
  };
  const paneToggleArchive = async (archived) => {
    if (!selectedMember) return;
    if (archived && !window.confirm("Archive this freelancer? They will no longer be able to log in or appear in the active directory. Historical project relations stay intact and this can be reversed.")) return;
    setPaneBusy(true);
    try {
      await api.post(`/v1/build/freelancers/${selectedMember.id}/${archived ? "archive" : "unarchive"}`);
      toast.success(archived ? "Freelancer archived" : "Freelancer reactivated");
      load();
    } catch (e) {
      // Cursor review fix batch (2026-02) item 6: prefer the backend's human-readable
      // `detail.error` (e.g. "This record has no linked Notion page...") over the raw
      // `detail.code` string ("notion_write_failed") that was all a founder saw before.
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail?.code || "Couldn't update archive status");
    } finally { setPaneBusy(false); }
  };
  const paneToggleAgencyVisible = async (visible) => {
    if (!selectedMember) return;
    try {
      await api.post(`/v1/build/freelancers/${selectedMember.id}/agency-visibility`, { visible });
      toast.success(visible ? "Now visible to Partner Agencies in Discover" : "Hidden from Partner Agencies");
      load();
    } catch { toast.error("Couldn't update agency visibility"); }
  };

  return (
    <div className="p-5 sm:p-10 space-y-8" data-testid="build-directory-page">
      <div className="rounded-lg border border-hairline bg-paper overflow-hidden" data-testid="build-directory-header">
        <div className="h-1.5 bg-brand" />
        <div className="px-6 sm:px-8 py-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-editorial text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink inline-flex items-center gap-2.5">
              <UserCheck className="h-7 w-7 text-brand" /> NJM Build
            </div>
            <p className="text-sm text-ink-soft mt-1.5 leading-relaxed">A controlled, verified network of freelancers, not an open marketplace.</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2 flex-wrap shrink-0" data-testid="build-agency-roster-toolbar">
              {!selectMode ? (
                <button type="button" onClick={() => setSelectMode(true)} data-testid="build-curate-roster-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-2 text-xs font-medium text-ink-soft hover:border-brand hover:text-ink transition-colors">
                  <Eye className="h-3.5 w-3.5" /> Curate agency-visible roster
                </button>
              ) : (
                <>
                  <span className="text-xs text-ink-soft" data-testid="build-selection-count">{selected.size} selected</span>
                  <button type="button" onClick={() => bulkSetVisibility(true)} disabled={bulkBusy || selected.size === 0} data-testid="build-bulk-show-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-tone-green-text px-3 py-2 text-xs font-semibold text-paper hover:opacity-90 disabled:opacity-50">
                    <Eye className="h-3.5 w-3.5" /> Show to agencies
                  </button>
                  <button type="button" onClick={() => bulkSetVisibility(false)} disabled={bulkBusy || selected.size === 0} data-testid="build-bulk-hide-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-2 text-xs font-medium text-ink hover:bg-surface2 disabled:opacity-50">
                    <EyeOff className="h-3.5 w-3.5" /> Hide from agencies
                  </button>
                  <button type="button" onClick={() => { setSelectMode(false); setSelected(new Set()); }} data-testid="build-cancel-select-btn"
                    className="text-xs text-faint hover:text-ink px-2 py-2">Cancel</button>
                </>
              )}
              <Link
                to="/admin/freelancers" data-testid="build-manage-freelancers-link"
                className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-4 py-2.5 text-sm font-medium text-ink-soft hover:border-brand hover:text-ink transition-colors"
              >
                Manage all
              </Link>
              <button type="button"
                data-testid="build-new-freelancer-btn" onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim transition-colors shrink-0"
              >
                <UserPlus className="h-4 w-4" /> New Freelancer
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-hairline bg-paper p-4 sm:p-5 flex items-end gap-3 flex-wrap" data-testid="build-filter-bar">
        <FilterSelect label="Role" testId="build-filter-role" value={role} onChange={(v) => setFilter("role", v)} options={roles} />
        <FilterSelect label="Location" testId="build-filter-location" value={location} onChange={(v) => setFilter("location", v)} options={locations} />
        <FilterSelect label="Availability" testId="build-filter-availability" value={availability} onChange={(v) => setFilter("availability", v)} options={AVAILABILITY_OPTIONS} />
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-faint">Skill</label>
          <input
            data-testid="build-filter-skill"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setFilter("skill", skillInput)}
            onBlur={() => setFilter("skill", skillInput)}
            placeholder="e.g. drone, colour grading…"
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-48 focus:border-brand outline-none transition-colors"
          />
        </div>
        {canManage && (
          <label className="flex items-center gap-2 pb-2 text-xs text-ink-soft cursor-pointer">
            <input type="checkbox" data-testid="build-filter-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-3.5 w-3.5" />
            Show archived &amp; pending approval
          </label>
        )}
        <label className="flex items-center gap-2 pb-2 text-xs text-ink-soft cursor-pointer">
          <input type="checkbox" data-testid="build-filter-show-test-accounts" checked={showTestAccounts} onChange={(e) => setShowTestAccounts(e.target.checked)} className="h-3.5 w-3.5" />
          Show test accounts
        </label>
        {(role || location || skill || availability) && (
          <button
            data-testid="build-filter-clear"
            onClick={() => { setSkillInput(""); setSp({}); }}
            className="text-xs text-faint hover:text-ink pb-2 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="flex gap-5 items-start" data-testid="build-directory-content">
        <div className="flex-1 min-w-0">
          {loading ? (
            <CardGridSkeleton count={9} testId="build-directory-skeleton" />
          ) : sortedItems.length === 0 ? (
            <EmptyState title="No freelancers match these filters" hint="Try clearing a filter or two." testId="build-directory-empty" />
          ) : (
            <div className="rounded-lg border border-hairline overflow-x-auto bg-paper">
              <div className="px-4 py-2 text-[11px] text-faint border-b border-hairline" data-testid="build-directory-sort-caption">
                Sorted A–Z · click a column to sort, click a row to open the member
              </div>
              <table className="w-full text-left" data-testid="build-directory-table">
                <thead>
                  <tr className="border-b border-hairline bg-surface2 text-[11px] uppercase tracking-wider text-faint">
                    {BUILD_TABLE_COLUMNS.map((col) => (
                      <th key={col.key} className={col.sortable === false ? "px-3 py-2 font-medium" : "px-3 py-2 font-medium cursor-pointer select-none"}
                        onClick={col.sortable === false ? undefined : () => toggleSort(col.key)}
                        data-testid={`build-directory-sort-${col.key}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {sort.key === col.key && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((f) => (
                    <MemberTableRow key={f.id} f={f}
                      selectMode={selectMode} selected={selected.has(f.id)} onToggleSelect={toggleSelect}
                      isActive={f.id === selectedId} onOpen={setSelectedId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedMember && (
          <MemberPane
            member={selectedMember}
            canManage={canManage}
            canToggleAgency={canToggleAgency}
            busy={paneBusy}
            onClose={() => setSelectedId(null)}
            onEdit={openPaneEdit}
            onToggleArchive={paneToggleArchive}
            onToggleAgencyVisible={paneToggleAgencyVisible}
          />
        )}
      </div>

      {creating && <CreateFreelancerModal onClose={() => setCreating(false)} onCreated={load} />}
      {paneFreelancer && (
        <EditFreelancerProfileModal freelancer={paneFreelancer} onClose={() => setPaneFreelancer(null)} onSaved={() => { setPaneFreelancer(null); load(); }} />
      )}
    </div>
  );
}

function FilterSelect({ label, testId, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-faint">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm w-44 focus:border-brand outline-none transition-colors"
      >
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

const EQUIPMENT_FIELDS = [
  { key: "camera_owned", label: "Camera", icon: Camera },
  { key: "lenses", label: "Lenses", icon: Aperture },
  { key: "lighting", label: "Lighting", icon: Sun },
  { key: "audio", label: "Audio", icon: Mic },
  { key: "drone", label: "Drone", icon: Plane },
  { key: "editing_setup", label: "Editing Setup", icon: Clapperboard },
];

const PORTFOLIO_LINKS = [
  { key: "website", label: "Website", icon: Globe },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "youtube", label: "YouTube", icon: Youtube },
  { key: "vimeo", label: "Vimeo", icon: Film },
  { key: "behance", label: "Behance", icon: ExternalLink },
];

export function FreelancerProfile() {
  const { id } = useParams();
  const { hasScope } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [reassigning, setReassigning] = useState(null);
  const [archiving, setArchiving] = useState(false);
  // Minimal Freelancer Onboarding V1, Step 2 (2026-08): INTERNAL_ROLES-only read panel —
  // read-only (no edit form yet, that's Step 4/5), gated by the SAME `freelancer_onboarding.
  // read` scope the backend enforces, so this simply never fetches/renders for a role that
  // can't see it anyway.
  const [onboarding, setOnboarding] = useState(null);
  const [revealedAccount, setRevealedAccount] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const load = () => {
    api.get(`/v1/build/freelancers/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 403 ? "restricted" : "not_found"));
  };
  const loadAssignments = () => {
    api.get(`/v1/build/assignments?freelancer_id=${id}`).then((r) => setAssignments(r.data.items || [])).catch(() => setAssignments([]));
  };
  const loadOnboarding = () => {
    if (!hasScope("freelancer_onboarding.read")) return;
    api.get(`/v1/build/onboarding/${id}`).then((r) => setOnboarding(r.data)).catch(() => setOnboarding(null));
  };
  useEffect(load, [id]);
  useEffect(loadAssignments, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadOnboarding, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Testing agent fix (2026-08, MEDIUM): toggles Show/Hide instead of only ever revealing —
  // previously the full number stayed on screen with no way to re-mask without a page reload.
  const revealAccountNumber = async () => {
    if (revealedAccount) {
      setRevealedAccount(null);
      return;
    }
    setRevealing(true);
    try {
      const r = await api.get(`/v1/build/onboarding/${id}/account-number/reveal`);
      setRevealedAccount(r.data.account_number || "—");
    } catch {
      toast.error("Couldn't reveal account number");
    } finally {
      setRevealing(false);
    }
  };

  // Testing agent fix (2026-08, HIGH): the amber banner had no way to actually clear the flag
  // from the UI — this is the ONLY way `bank_change_unacknowledged` is cleared (never by the
  // freelancer's own save, see update_my_onboarding).
  const acknowledgeBankChange = async () => {
    setAcknowledging(true);
    try {
      await api.post(`/v1/build/onboarding/${id}/acknowledge-bank-change`);
      toast.success("Bank change acknowledged");
      loadOnboarding();
    } catch {
      toast.error("Couldn't acknowledge bank change");
    } finally {
      setAcknowledging(false);
    }
  };

  const toggleArchive = async (archived) => {
    if (archived && !window.confirm("Archive this freelancer? They will no longer be able to log in or appear in the active directory. Historical project relations stay intact and this can be reversed.")) return;
    setArchiving(true);
    try {
      await api.post(`/v1/build/freelancers/${id}/${archived ? "archive" : "unarchive"}`);
      toast.success(archived ? "Freelancer archived" : "Freelancer reactivated");
      load();
    } catch (e) {
      // Cursor review fix batch (2026-02) item 6: prefer the backend's human-readable
      // `detail.error` (e.g. "This record has no linked Notion page...") over the raw
      // `detail.code` string ("notion_write_failed") that was all a founder saw before.
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail?.code || "Couldn't update archive status");
    } finally {
      setArchiving(false);
    }
  };

  const toggleAgencyVisible = async (visible) => {
    try {
      await api.post(`/v1/build/freelancers/${id}/agency-visibility`, { visible });
      toast.success(visible ? "Now visible to Partner Agencies in Discover" : "Hidden from Partner Agencies");
      load();
    } catch { toast.error("Couldn't update agency visibility"); }
  };

  if (error) return <div className="p-8 text-sm text-faint" data-testid="build-profile-error">{error === "restricted" ? "Access Restricted" : "Freelancer not found"}</div>;
  if (!data) return <DetailSkeleton testId="build-profile-skeleton" />;

  const f = data.freelancer;
  // Commercial/Performance blocks are ONLY rendered if the backend actually returned these
  // fields — FIELD_GATES already strips them server-side for any non-NJM role, so this is
  // graceful degradation, not the real security boundary (that's server-side).
  const hasCommercial = f.day_rate !== undefined || f.half_day_rate !== undefined || f["half-day_rate"] !== undefined;
  const hasPerformance = f.njm_rating !== undefined;
  const equipment = EQUIPMENT_FIELDS.filter((e) => f[e.key]);
  const portfolio = PORTFOLIO_LINKS.filter((p) => f[p.key]);
  const recognition = [f.achievements, f.recognition].filter(Boolean);
  const gearSoftware = [...(f.gear || []), ...(f.editing_software || []), ...(f.ai_creator_software || [])];

  return (
    <div className="p-5 sm:p-10 space-y-10" data-testid="build-profile-page">
      <div className="flex items-start justify-between gap-5 flex-wrap">
        <div className="flex items-start gap-5 flex-wrap">
          {f.profile_image ? (
            <ProfileAvatar freelancerId={f.id} hasPhoto alt={f.name} className="h-24 w-24 rounded-full object-cover ring-1 ring-hairline shadow-sm" testId="build-profile-detail-avatar" />
          ) : (
            <div className="h-24 w-24 rounded-full bg-brand/15 border border-brand/30 flex items-center justify-center text-3xl font-semibold text-ink">
              {(f.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-editorial text-4xl tracking-tight" data-testid="build-profile-name">{f.name}</h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {f.primary_role && <Chip>{f.primary_role}</Chip>}
              {(f.secondary_roles || []).filter((r) => r !== f.primary_role).map((r) => <Chip key={r}>{r}</Chip>)}
              <AvailabilityChip availability={f.availability} />
              <VerifiedBadge badges={f.badges} />
              {f.archived && (
                <span data-testid="build-profile-archived-badge" className="rounded-full bg-red-500/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-red-600">
                  Archived
                </span>
              )}
              {!f.archived && f.approved === false && (
                <span data-testid="build-profile-pending-approval-badge" className="rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-amber-600">
                  Pending Approval
                </span>
              )}
              {f.test_account && (
                <span data-testid="build-profile-test-account-badge" className="rounded-full bg-sky-500/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-sky-600">
                  Test Account
                </span>
              )}
              {f.needs_interview_review && (
                <span data-testid="build-profile-needs-review-badge" className="rounded-full bg-orange-500/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-orange-600">
                  Needs Interview Review
                </span>
              )}
            </div>
            {f.canonical_location && (
              <div className="text-sm text-ink-soft mt-3 inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {f.canonical_location}</div>
            )}
          </div>
        </div>
        {/* Phase 1 of the Operational Control Upgrade (2026-02): only rendered when the backend
            actually granted freelancer_profile.write (founder/partner/pm) — graceful UI
            degradation, the real boundary is server-side in update_freelancer_profile(). */}
        {hasScope("freelancer_profile.write") && (
          <div className="flex items-center gap-2 shrink-0">
            {hasScope("build.write") && !f.archived && (
              f.partner_agency_visible ? (
                <button data-testid="build-profile-agency-hide-btn" onClick={() => toggleAgencyVisible(false)}
                  title="This freelancer is shown in the Partner Agency Discover roster. Click to hide."
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-500/20 transition-colors">
                  <Eye className="h-3.5 w-3.5" /> Agency-visible
                </button>
              ) : (
                <button data-testid="build-profile-agency-show-btn" onClick={() => toggleAgencyVisible(true)}
                  title="Show this freelancer in the Partner Agency Discover roster."
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3.5 py-2 text-xs font-medium text-ink-soft hover:border-brand hover:text-ink transition-colors">
                  <EyeOff className="h-3.5 w-3.5" /> Not agency-visible
                </button>
              )
            )}
            <button
              data-testid="build-profile-edit-btn" onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3.5 py-2 text-xs font-medium text-ink-soft hover:border-brand hover:text-ink transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit Profile
            </button>
            {f.archived ? (
              <button
                data-testid="build-profile-unarchive-btn" onClick={() => toggleArchive(false)} disabled={archiving}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3.5 py-2 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10 transition-colors disabled:opacity-60"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reactivate
              </button>
            ) : (
              <button
                data-testid="build-profile-archive-btn" onClick={() => toggleArchive(true)} disabled={archiving}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 px-3.5 py-2 text-xs font-medium text-red-600 hover:bg-red-500/10 transition-colors disabled:opacity-60"
              >
                <Archive className="h-3.5 w-3.5" /> Archive
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <EditFreelancerProfileModal freelancer={f} onClose={() => setEditing(false)} onSaved={load} />
      )}


      {f.bio && (
        <Section title="About">
          <div className="px-5 py-4 text-sm text-ink/90 whitespace-pre-wrap leading-relaxed">{f.bio}</div>
        </Section>
      )}

      {portfolio.length > 0 && (
        <Section title="Portfolio">
          <div className="px-5 py-4 flex flex-wrap gap-3">
            {portfolio.map(({ key, label, icon: Icon }) => {
              const url = normalizeUrl(f[key], key);
              return url ? (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`build-portfolio-link-${key}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-ink transition-colors">
                  <Icon className="h-3.5 w-3.5" /> {label}
                </a>
              ) : null;
            })}
            {f.work_links && (
              <div className="w-full mt-1 space-y-1">
                {f.work_links.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean).map((raw, i) => {
                  const url = normalizeUrl(raw);
                  return url ? (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" data-testid={`build-work-link-${i}`}
                      className="block text-xs text-ink-soft hover:text-brand truncate transition-colors">{raw}</a>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Equipment Experience">
        <div className="px-5 py-3 text-[11px] text-faint italic">Capability signal only — never a rental source or cost input.</div>
        {equipment.length === 0 ? (
          <EmptyState title="No equipment experience listed" testId="build-profile-equipment-empty" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 px-5 pb-5">
            {equipment.map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-start gap-2 text-sm" data-testid={`build-equipment-${key}`}>
                <Icon className="h-4 w-4 text-faint mt-0.5 shrink-0" />
                <div><span className="text-ink-soft">{label}: </span><span className="text-ink/90">{f[key]}</span></div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Freelancer Profile Data Audit (2026-02, Phase 3) — PM/Founder layer: same
          Specialisation/Gear & Software/Preferred Project Types groups as the Self view and
          the Public peer directory, reusing the SAME shared components so all 3 surfaces stay
          in visual lockstep. `get_freelancer` now computes the identical `specialisation`/
          `gear` composites the Public/Self/Interview endpoints already did. */}
      {gearSoftware.length > 0 && (
        <Section title="Gear & Software">
          <div className="px-5 py-4 space-y-3.5">
            <ChipRow label="Gear" items={f.gear} testId="build-profile-gear-chips" />
            <ChipRow label="Editing / Post-Production Software" items={f.editing_software} testId="build-profile-editing-software-chips" />
            <ChipRow label="AI Software" items={f.ai_creator_software} testId="build-profile-ai-software-chips" />
          </div>
        </Section>
      )}

      {(f.preferred_project_types || []).length > 0 && (
        <Section title="Preferred Project Types">
          <div className="px-5 py-4 flex flex-wrap gap-2" data-testid="build-profile-project-types-chips">
            {f.preferred_project_types.map((t) => <Chip key={t}>{t}</Chip>)}
          </div>
        </Section>
      )}

      {f.specialisation?.role_specific && Object.keys(f.specialisation.role_specific).length > 0 && (
        <Section title="Specialisation">
          <div className="px-5 py-4">
            <SpecialisationRows specialisation={f.specialisation} testIdPrefix="build-profile-specialisation" />
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Stat label="Experience" value={jobExperienceLabel(f.job_experience) !== "—" ? jobExperienceLabel(f.job_experience) : (f.experience_years ? `${f.experience_years} yrs` : "—")} />
        <Stat label="Languages" value={f.languages || "—"} icon={Languages} />
      </div>

      <Section title="NJM Projects">
        {(data.njm_projects || []).length === 0 ? (
          <EmptyState title="No linked NJM projects yet" testId="build-profile-projects-empty" />
        ) : (
          <div className="divide-y divide-hairline">
            {data.njm_projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`build-profile-project-${p.id}`} className="flex items-center justify-between px-5 py-3.5 text-sm text-ink hover:bg-brand/[0.06] transition-colors">
                {p.name} <ExternalLink className="h-3.5 w-3.5 text-faint" />
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Phase 2 of the Operational Control Upgrade (2026-02): 2nd entry point into the SAME
          Reassignment workflow AssignmentEngine.jsx exposes on the project side — full history,
          nothing hard-deleted, so a Cancelled row from a past reassignment stays visible here. */}
      {hasScope("build.read") && (
        <Section title="Assignment History">
          {assignments.length === 0 ? (
            <EmptyState title="No assignments yet" testId="build-profile-assignments-empty" />
          ) : (
            <div className="divide-y divide-hairline">
              {assignments.map((a) => (
                <div key={a.id} data-testid={`build-profile-assignment-row-${a.id}`} className="flex items-center justify-between gap-3 px-5 py-3.5 text-sm flex-wrap">
                  <div className="min-w-0">
                    <div className="text-ink truncate">{a.project_name || "—"}</div>
                    <div className="text-[10px] text-faint mt-0.5">
                      {a.participation_log_only ? "Participation Log" : a.status}
                      {a.scope && ` · ${a.scope}`}
                    </div>
                  </div>
                  {hasScope("build.write") && !a.participation_log_only && a.valid_next_statuses?.includes("Cancelled") && (
                    <button onClick={() => setReassigning(a)} data-testid={`build-profile-reassign-btn-${a.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-2.5 py-1 text-[11px] text-ink-soft hover:border-red-400 hover:text-red-600 shrink-0">
                      Reassign
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {reassigning && (
        <ReassignAssignmentModal assignment={reassigning} onClose={() => setReassigning(null)} onDone={loadAssignments} />
      )}

      {recognition.length > 0 && (
        <Section title="Recognition">
          <div className="px-5 py-4 space-y-2.5 text-sm text-ink/90">
            {recognition.map((r, i) => <div key={i} className="inline-flex items-start gap-2"><Award className="h-4 w-4 text-faint mt-0.5 shrink-0" />{r}</div>)}
          </div>
        </Section>
      )}

      {/* Commercial + Performance — NJM roles ONLY. Absent from the response entirely (backend
          FIELD_GATES) for any freelancer/production_partner viewer, so these blocks simply never
          render for them — no client-side role check needed as the real security boundary. */}
      {hasCommercial && (
        <div className="rounded-lg border border-brand/20 bg-brand/[0.03] p-5" data-testid="build-profile-commercial-block">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink mb-3.5 inline-flex items-center gap-1.5"><Wallet className="h-3 w-3 text-brand" /> Commercial (NJM Internal)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Stat label="Day Rate" value={f.day_rate ?? "—"} />
            <Stat label="Half-Day Rate" value={f.half_day_rate ?? f["half-day_rate"] ?? "—"} />
            <Stat label="Negotiated Rates" value={f.negotiated_rates || "—"} />
            <Stat label="Project Rates" value={f.project_rates || "—"} />
          </div>
        </div>
      )}

      {hasPerformance && (
        <div className="rounded-lg border border-hairline bg-paper p-5" data-testid="build-profile-performance-block">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-faint mb-3.5 inline-flex items-center gap-1.5"><Star className="h-3 w-3" /> Performance (NJM Internal)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="NJM Rating" value={f.njm_rating ?? "—"} />
            <Stat label="Reliability" value={f.reliability_score ?? "—"} />
            <Stat label="Quality" value={f.quality_score ?? "—"} />
            <Stat label="Communication" value={f.communication_score ?? "—"} />
          </div>
        </div>
      )}

      {/* Minimal Freelancer Onboarding V1, Step 2 (2026-08) — INTERNAL_ROLES-only (founder/
          partner/finance/pm), read-only. Only ever fetched/rendered when the backend actually
          granted `freelancer_onboarding.read` — same graceful-degradation convention as
          Commercial/Performance above; the real boundary is server-side. */}
      {hasScope("freelancer_onboarding.read") && onboarding && (
        <div className="rounded-lg border border-brand/20 bg-brand/[0.03] p-5" data-testid="build-profile-onboarding-block">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink mb-3.5 inline-flex items-center gap-1.5">
            <Wallet className="h-3 w-3 text-brand" /> Onboarding &amp; Payment Details (NJM Internal)
          </div>
          {!onboarding.exists ? (
            <EmptyState title="Not yet submitted" testId="build-profile-onboarding-empty" />
          ) : (
            <div className="space-y-4">
              {onboarding.onboarding.bank_change_unacknowledged && (
                <div data-testid="build-profile-onboarding-bank-change-flag" className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-xs text-amber-700 flex items-center justify-between gap-3">
                  <span>Bank details changed since last confirmation — needs review before next payment.</span>
                  {hasScope("freelancer_onboarding.write") && (
                    <button onClick={acknowledgeBankChange} disabled={acknowledging} data-testid="build-profile-onboarding-acknowledge-btn"
                      className="shrink-0 rounded-md bg-amber-500/20 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-500/30 transition-colors disabled:opacity-60">
                      {acknowledging ? "Acknowledging…" : "Acknowledge"}
                    </button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Stat label="Name" value={onboarding.onboarding.name || "—"} />
                <Stat label="Email" value={onboarding.onboarding.email || "—"} />
                <Stat label="Phone" value={onboarding.onboarding.phone || "—"} />
                <Stat label="Address" value={onboarding.onboarding.address || "—"} />
                <Stat label="PAN / Gov ID" value={onboarding.onboarding.pan_gov_id || "—"} />
                <Stat label="GST Registered" value={onboarding.onboarding.gst_registered === true ? "Yes" : onboarding.onboarding.gst_registered === false ? "No" : "—"} />
                <Stat label="GST Number" value={onboarding.onboarding.gst_number || "—"} />
                <Stat label="Account Holder" value={onboarding.onboarding.account_holder_name || "—"} />
                <Stat label="Bank Name" value={onboarding.onboarding.bank_name || "—"} />
                <div className="rounded-lg border border-hairline bg-paper p-5">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-faint">Account Number</div>
                  <div className="text-sm mt-1.5 text-ink/90 flex items-center gap-2" data-testid="build-profile-onboarding-account-number">
                    {revealedAccount || onboarding.onboarding.account_number || "—"}
                    {onboarding.onboarding.account_number && (
                      <button onClick={revealAccountNumber} disabled={revealing} data-testid="build-profile-onboarding-reveal-btn"
                        className="text-[11px] text-brand hover:underline disabled:opacity-60">
                        {revealing ? "Revealing…" : revealedAccount ? "Hide" : "Show"}
                      </button>
                    )}
                  </div>
                </div>
                <Stat label="Branch" value={onboarding.onboarding.branch || "—"} />
                <Stat label="IFSC" value={onboarding.onboarding.ifsc_code || "—"} />
                <Stat label="Emergency Contact" value={onboarding.onboarding.emergency_contact_name || "—"} />
                <Stat label="Relationship" value={onboarding.onboarding.emergency_contact_relationship || "—"} />
                <Stat label="Emergency Phone" value={onboarding.onboarding.emergency_contact_phone || "—"} />
                <Stat label="Emergency Email" value={onboarding.onboarding.emergency_contact_email || "—"} />
              </div>
              <div className="text-[11px] text-faint">Last confirmed: {onboarding.onboarding.confirmed_at ? new Date(onboarding.onboarding.confirmed_at).toLocaleString() : "—"}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-hairline bg-paper p-5">
    <div className="text-[11px] font-medium uppercase tracking-wider text-faint">{label}</div>
    <div className="text-sm mt-1.5 text-ink/90">{value}</div>
  </div>
);
const Chip = ({ children }) => <span className="rounded-full bg-hairline px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-ink-soft">{children}</span>;
const Section = ({ title, children }) => (
  <section className="rounded-lg border border-hairline bg-paper">
    <header className="border-b border-hairline px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-faint">{title}</header>
    {children}
  </section>
);
